import { useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import type { QuickCreateType } from "./QuickCreateModal";

export type LinkExistingType = QuickCreateType | "note";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  type: LinkExistingType;
  meetingId: string;
  onLinked?: () => void;
};

const TYPE_CONFIG: Record<LinkExistingType, { label: string; endpoint: string }> = {
  action: { label: "Action", endpoint: "/actions" },
  task: { label: "Task", endpoint: "/tasks" },
  idea: { label: "Idea", endpoint: "/ideas" },
  decision: { label: "Decision", endpoint: "/decisions" },
  milestone: { label: "Milestone", endpoint: "/milestones" },
  note: { label: "Note", endpoint: "/notes" },
};

type SearchItem = {
  id: string;
  title: string;
  code?: string;
  status?: string;
};

export function LinkExistingModal({
  isOpen,
  onClose,
  type,
  meetingId,
  onLinked,
}: Props) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  const config = TYPE_CONFIG[type];

  const [search, setSearch] = useState("");
  const [results, setResults] = useState<SearchItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [searched, setSearched] = useState(false);

  async function handleSearch() {
    if (!search.trim()) return;
    setLoading(true);
    try {
      const res = await apiFetch<{ data: SearchItem[] }>(config.endpoint, {
        token: accessToken,
        query: { search: search.trim(), limit: 20, page: 1 },
      });
      setResults(res.data ?? []);
      setSearched(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleLink() {
    if (selected.size === 0) return;
    setLinking(true);
    try {
      await Promise.all(
        [...selected].map((itemId) =>
          apiFetch(`${config.endpoint}/${itemId}`, {
            method: "PUT",
            body: { meetingId },
            token: accessToken,
          }),
        ),
      );
      qc.invalidateQueries({ queryKey: ["meetings"] });
      onLinked?.();
      onClose();
    } catch (err) {
      console.error("[LinkExisting] failed:", err);
    } finally {
      setLinking(false);
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-[9999] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-white shadow-2xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-base font-semibold text-foreground">
            Link Existing {config.label}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                strokeWidth={1.5}
              />
              <Input
                placeholder={`Search ${config.label.toLowerCase()}s...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="pl-9"
              />
            </div>
            <Button type="button" onClick={handleSearch} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                "Search"
              )}
            </Button>
          </div>

          <div className="max-h-[300px] overflow-y-auto rounded-lg border" style={{ borderColor: "hsl(210,30%,85%)" }}>
            {!searched ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Search to find {config.label.toLowerCase()}s to link
              </div>
            ) : results.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No results found
              </div>
            ) : (
              results.map((item) => (
                <label
                  key={item.id}
                  className="flex cursor-pointer items-center gap-3 border-b px-4 py-3 transition-colors hover:bg-[hsl(210,50%,98%)] last:border-b-0"
                  style={{ borderColor: "hsl(210,30%,90%)" }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    onChange={() => toggleSelect(item.id)}
                    className="h-4 w-4 rounded border-gray-300 accent-primary"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground">
                      {item.title}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {item.code && (
                        <span className="font-mono">{item.code}</span>
                      )}
                      {item.status && (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                          style={{
                            background: "hsl(210,16%,93%)",
                            color: "hsl(215,16%,40%)",
                          }}
                        >
                          {item.status}
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-muted-foreground">
              {selected.size} selected
            </span>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={selected.size === 0 || linking}
                onClick={handleLink}
              >
                {linking && (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
                )}
                Link Selected
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
